// Μικρά στοιχεία φόρμας του editor κάτοψης, στο ύφος του ταμείου.

import React from 'react';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600',
  secondary: 'bg-surface-1 text-t1 border border-line hover:bg-surface-2',
  ghost: 'text-t2 hover:bg-surface-2 border border-transparent',
  danger: 'bg-surface-1 text-red-600 dark:text-red-400 border border-red-300/60 hover:bg-red-50 dark:hover:bg-red-950/30',
};

export function Btn({ variant = 'secondary', size = 'md', icon, className = '', children, ...rest }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'icon'; icon?: React.ReactNode }) {
  const sz = size === 'sm' ? 'h-9 px-3 text-[13px] gap-1.5' : size === 'icon' ? 'h-10 w-10 justify-center' : 'h-10 px-4 text-[14px] gap-2';
  return (
    <button type="button" {...rest}
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${VARIANT[variant]} ${sz} ${className}`}>
      {icon}{children}
    </button>
  );
}

export const inputClass = 'h-10 w-full rounded-lg border border-line bg-surface-2 px-3 text-[14px] text-t1 placeholder:text-t4 focus:border-blue-500 focus:outline-none';

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[13px] font-medium text-t2">{label}</span>
      {children}
      {error ? <span className="text-[12.5px] text-red-600 dark:text-red-400">{error}</span> : hint ? <span className="text-[12px] text-t3">{hint}</span> : null}
    </label>
  );
}

export function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label} className="grid gap-1.5">
      <span className="text-[13px] font-medium text-t2">{label}</span>
      {children}
    </div>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-lg bg-surface-2 p-1">
      {options.map(o => (
        <button key={String(o.value)} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
          className={`h-8 flex-1 whitespace-nowrap rounded-md px-2.5 text-[13px] font-medium transition-colors ${value === o.value ? 'bg-surface-1 text-t1 shadow-sm' : 'text-t3 hover:text-t1'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-3 py-1 text-left disabled:opacity-40">
      <span className="grid gap-0.5"><span className="text-[14px] text-t1">{label}</span>{hint && <span className="text-[12px] text-t3">{hint}</span>}</span>
      <span className={`relative block h-6 w-10 shrink-0 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-line-strong'}`}>
        <span className={`absolute left-0 top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[19px]' : 'translate-x-[3px]'}`} />
      </span>
    </button>
  );
}

/** −/+ με την τιμή στη μέση (θέσεις, άτομα, μέτρα). */
export function Stepper({ value, display, min, max, step = 1, onChange, less, more }: {
  value: number; display?: string; min: number; max?: number; step?: number; onChange: (v: number) => void; less: string; more: string;
}) {
  return (
    <div className="inline-flex h-10 shrink-0 items-center rounded-lg border border-line bg-surface-2">
      <button type="button" aria-label={less} disabled={value - step < min} onClick={() => onChange(value - step)} className="grid h-10 w-10 place-items-center text-[18px] text-t2 disabled:opacity-30">−</button>
      <span className="min-w-[64px] text-center text-[14px] font-semibold tabular-nums text-t1">{display ?? value}</span>
      <button type="button" aria-label={more} disabled={max != null && value + step > max} onClick={() => onChange(value + step)} className="grid h-10 w-10 place-items-center text-[18px] text-t2 disabled:opacity-30">+</button>
    </div>
  );
}

export function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="grid gap-0.5"><span className="text-[14px] text-t1">{label}</span>{hint && <span className="text-[12px] text-t3">{hint}</span>}</span>
      {children}
    </div>
  );
}

export function Note({ tone, children }: { tone?: 'error' | 'warn' | 'ok'; children: React.ReactNode }) {
  const Icon = tone === 'ok' ? CheckCircle : tone ? AlertTriangle : Info;
  const look = tone === 'error' ? 'bg-red-500/10 text-red-700 dark:text-red-300'
    : tone === 'warn' ? 'bg-amber-500/12 text-amber-700 dark:text-amber-300'
      : tone === 'ok' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-surface-2 text-t2';
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px] leading-snug ${look}`}>
      <Icon className="mt-px h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export function Heading({ children, sub }: { children: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="mt-1 flex items-baseline justify-between gap-2">
      <h3 className="text-[14px] font-semibold text-t1">{children}</h3>
      {sub != null && <span className="text-[12px] text-t3">{sub}</span>}
    </div>
  );
}
